/**
 * Fetchable Plugin for Riot.js
 * 
 * Automatically enhances components that implement getData() with anti-infinite loop
 * data fetching capabilities. Components can opt-in by:
 * - Implementing a getData() method (auto-detection)
 * - Setting fetchable: true in component config
 * - Being included in plugin configuration
 */

export function createFetchablePlugin(options = {}) {
    const config = {
        // Global defaults
        autoDetect: true, // Automatically detect components with getData method
        globalRefreshTrigger: 'refreshTrigger', // Default prop name for refresh triggers
        debugMode: false, // Enable debug logging
        ...options
    }

    return function fetchablePlugin(component) {
        // Skip if component doesn't opt-in to fetchable behavior
        if (!shouldEnhanceComponent(component, config)) {
            return component
        }

        if (config.debugMode) {
            console.log('[Fetchable Plugin] Enhancing component:', component.name || 'unnamed')
        }

        // Enhance component state with fetchable properties
        enhanceComponentState(component)
        
        // Add fetchable methods
        enhanceComponentMethods(component, config)
        
        // Enhance lifecycle methods
        enhanceComponentLifecycle(component, config)

        return component
    }
}

/**
 * Determine if a component should be enhanced with fetchable capabilities
 */
function shouldEnhanceComponent(component, config) {
    // Explicit opt-in via component config
    if (component.fetchable === true) {
        return true
    }

    // Explicit opt-out
    if (component.fetchable === false) {
        return false
    }

    // Auto-detect via getData method
    if (config.autoDetect && typeof component.getData === 'function') {
        return true
    }

    return false
}

/**
 * Add fetchable state properties to component
 */
function enhanceComponentState(component) {
    const fetchableState = {
        data: null,
        loading: false,
        error: null,
        // Internal flags to prevent infinite loops
        _fetchInProgress: false,
        _lastFetchId: null,
        _mountFetchComplete: false
    }

    // Merge with existing state
    component.state = {
        ...fetchableState,
        ...(component.state || {})
    }
}

/**
 * Add fetchable methods to component
 */
function enhanceComponentMethods(component, config) {
    /**
     * Core fetch method with loop prevention
     * @param {boolean} forceRefresh - Force fetch even if one is in progress
     * @param {string} source - Source of the fetch ('mount', 'parent', 'manual')
     */
    component.fetchData = async function(forceRefresh = false, source = 'manual') {
        // Prevent concurrent fetches unless forced
        if (this.state._fetchInProgress && !forceRefresh) {
            return
        }

        // Generate unique fetch ID to handle race conditions
        const fetchId = Date.now() + Math.random()
        
        this.update({ 
            loading: true, 
            _fetchInProgress: true,
            _lastFetchId: fetchId,
            error: null
        })

        try {
            // Call the component's getData method (must be implemented by component)
            if (typeof this.getData !== 'function') {
                throw new Error('Component must implement getData() method to use fetchable plugin')
            }

            const data = await this.getData()
            
            // Only update if this is still the latest fetch
            if (this.state._lastFetchId === fetchId) {
                this.update({ 
                    data, 
                    loading: false, 
                    error: null,
                    _fetchInProgress: false,
                    _mountFetchComplete: source === 'mount' ? true : this.state._mountFetchComplete
                })
            }
        } catch (error) {
            // Only update if this is still the latest fetch
            if (this.state._lastFetchId === fetchId) {
                this.update({ 
                    error: error.message, 
                    loading: false,
                    _fetchInProgress: false,
                    data: null,
                    _mountFetchComplete: source === 'mount' ? true : this.state._mountFetchComplete
                })
            }
        }
    }

    /**
     * Public API for manual refresh (callable by parent components)
     */
    component.refresh = function() {
        return this.fetchData(true, 'parent')
    }

    /**
     * Check if component should refetch based on prop changes
     * Override this method in your component to define custom refetch logic
     */
    if (!component.shouldRefetch) {
        component.shouldRefetch = function(prevProps) {
            // Default: refetch if refreshTrigger prop changes
            const triggerProp = config.globalRefreshTrigger
            return (
                prevProps && 
                this.props[triggerProp] !== prevProps[triggerProp]
            )
        }
    }
}

/**
 * Enhance component lifecycle methods
 */
function enhanceComponentLifecycle(component, config) {
    // Store original lifecycle methods
    const originalOnMounted = component.onMounted
    const originalOnUpdated = component.onUpdated

    /**
     * Enhanced onMounted - fetch data on mount
     */
    component.onMounted = function() {
        if (config.debugMode) {
            console.log('[Fetchable Plugin] Component mounted, fetching data')
        }
        
        // Fetch data on mount
        this.fetchData(false, 'mount')

        // Call original onMounted if it exists
        if (originalOnMounted) {
            originalOnMounted.call(this)
        }
    }

    /**
     * Enhanced onUpdated - handle prop changes that should trigger refetch
     */
    component.onUpdated = function(prevProps, prevState) {
        // Call original onUpdated first
        if (originalOnUpdated) {
            originalOnUpdated.call(this, prevProps, prevState)
        }

        // Simple, bulletproof infinite loop prevention
        // Only check for refetch if props actually changed (not state changes)
        if (!prevProps || !this.state._mountFetchComplete || this.state._fetchInProgress) {
            return
        }
        
        // Only proceed if this was a prop change, not a state change
        const isStateOnlyUpdate = prevState && (
            prevState.loading !== this.state.loading ||
            prevState._fetchInProgress !== this.state._fetchInProgress ||
            prevState.data !== this.state.data ||
            prevState.error !== this.state.error
        )
        
        if (isStateOnlyUpdate) {
            return // Skip refetch for state-only updates
        }
        
        // Check if we should refetch based on prop changes
        if (this.shouldRefetch(prevProps)) {
            if (config.debugMode) {
                console.log('[Fetchable Plugin] Props changed, refetching data')
            }
            this.fetchData(false, 'parent')
        }
    }
}

// Export default plugin instance with default configuration
export const fetchablePlugin = createFetchablePlugin()